import { Link } from 'react-router-dom';
import { ShieldCheck, Handshake, MessageCircle, Compass } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

/* Sourcing & Testing — provenance page. Explains our commitment to
 * "Worldwide Best" tried-and-tested gear and the current negotiation status
 * with our shortlisted manufacturers. Highland-themed hero. */

const HERO =
  'https://images.unsplash.com/photo-1680936613337-fc829882b375?fm=jpg&q=70&w=2000&auto=format&fit=crop';

const NEGOTIATIONS = [
  {
    category: 'Tents & Tarpaulins',
    status: 'Final technical review',
    detail:
      'Two shortlisted factories in Jiangsu and Zhejiang. Fire-retardancy (BS 7837), REACH, and hydrostatic head testing already returned. Sample tents pitched on the estate through the wet January.',
  },
  {
    category: 'Sleeping Bags & Bedrolls',
    status: 'Sample round two',
    detail:
      'RDS-certified goose-down supplier confirmed; comfort ratings independently re-tested at EN 13537. Custom embroidered label prototypes back from the loom.',
  },
  {
    category: 'Survival & First-Aid Kit',
    status: 'Compliance verification',
    detail:
      'BS 8599-1 personal-kit spec, ISO 13485 medical-device grade sterile items, UN 38.3 lithium-cell transport for the powered lights. One factory offered a vulcanised rubber patch mounting our mark direct to the pouch.',
  },
  {
    category: 'Campfire Tools (wooden-handled)',
    status: 'Heat-brand trials',
    detail:
      'Ash and hickory handles arriving next month for pyrography trials \u2014 our brand mark burnt into the wood at manufacture. If the char is deep and even, we commit.',
  },
];

export default function SourcingPage() {
  return (
    <>
      <Navigation />
      <main
        data-testid="sourcing-page"
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-24"
      >
        {/* Hero */}
        <div className="relative h-[52svh] min-h-[380px] w-full overflow-hidden">
          <img src={HERO} alt="Soft misty Highland range at sunrise" className="absolute inset-0 w-full h-full object-cover" />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(4,20,10,0.60) 0%, rgba(4,20,10,0.35) 40%, rgba(4,20,10,0.85) 100%)',
            }}
          />
          <div className="absolute inset-x-0 bottom-0 max-w-5xl mx-auto px-6 lg:px-10 pb-10 text-[var(--brand-parchment)]">
            <span className="font-eyebrow !text-[var(--brand-gold)]">Sourcing &amp; Testing</span>
            <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
              The <span className="italic text-[var(--brand-gold)]">worldwide best</span>,<br />tried on the estate.
            </h1>
          </div>
        </div>

        {/* Vision block */}
        <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-16">
          <blockquote className="border-l-4 border-[var(--brand-gold)] bg-[var(--brand-forest)]/5 px-6 py-6">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--brand-gold-soft)]">Our Vision</span>
            <p className="mt-2 font-display italic text-xl leading-snug text-[var(--brand-forest-deep)]">
              &ldquo;Our team is committed to procuring the worldwide best tried and tested eco-friendly, user-friendly, fun and adventurous transport on the planet.&rdquo;
            </p>
          </blockquote>

          <p className="mt-10 text-[var(--brand-muted)] text-lg leading-relaxed">
            Every product that carries the estate mark has to earn it. We&rsquo;re a small
            Perthshire outfit, drop-shipping direct from factory to your door, which means
            we can&rsquo;t rely on a UK warehouse to catch mistakes. So we do the catching upstream.
          </p>
          <p className="mt-4 text-[var(--brand-muted)] text-lg leading-relaxed">
            We are currently in <span className="font-semibold text-[var(--brand-forest)]">final technical negotiations
            with the world&rsquo;s leading verified manufacturers</span> for tents, sleeping bags,
            and survival equipment to meet our &ldquo;Heritage Estate&rdquo; standards. Every candidate
            factory is audited on Alibaba Trade Assurance, third-party verified (SGS or BSCI),
            and asked to sample product to us in-country before we commit.
          </p>
        </section>

        {/* Four pillars */}
        <section className="max-w-5xl mx-auto px-6 lg:px-10 mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Compass, title: 'Worldwide Best', body: 'Shortlisted only from factories already exporting to REI, Decathlon or Alpkit \u2014 they understand UK weather and compliance.' },
            { icon: ShieldCheck, title: 'Independently Tested', body: 'Third-party test reports (SGS, Intertek) supplied with every shipment. No self-certification.' },
            { icon: Handshake, title: 'Trade Assurance Only', body: 'Every payment flows through Alibaba Trade Assurance escrow. No side channels. No WhatsApp deals.' },
            { icon: MessageCircle, title: 'Estate-Tested', body: 'Sample items pitched, packed and slept in on the estate before we agree a production run.' },
          ].map((p) => (
            <div key={p.title} className="border-t border-[var(--brand-olive)]/40 pt-5">
              <p.icon size={26} strokeWidth={1.4} className="text-[var(--brand-gold)]" />
              <div className="mt-3 font-display text-xl text-[var(--brand-forest)]">{p.title}</div>
              <p className="text-sm mt-1.5 opacity-90 leading-relaxed text-[var(--brand-muted)]">{p.body}</p>
            </div>
          ))}
        </section>

        {/* Live negotiations table */}
        <section className="max-w-5xl mx-auto px-6 lg:px-10 mt-20">
          <h2 className="font-display text-3xl sm:text-4xl text-[var(--brand-forest)]">Where we are, honestly.</h2>
          <p className="mt-3 text-[var(--brand-muted)]">
            A live snapshot of the categories we&rsquo;re currently qualifying suppliers on.
            Updated whenever the status changes &mdash; not on a marketing calendar.
          </p>

          <div className="mt-8 divide-y divide-[var(--brand-olive)]/30 border-y border-[var(--brand-olive)]/30">
            {NEGOTIATIONS.map((row) => (
              <div
                key={row.category}
                data-testid={`sourcing-row-${row.category.split(' ')[0].toLowerCase()}`}
                className="grid md:grid-cols-[1.2fr_1fr_2fr] gap-4 md:gap-8 py-6"
              >
                <div className="font-display text-xl text-[var(--brand-forest-deep)]">{row.category}</div>
                <div className="inline-flex self-start items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-forest)] bg-[var(--brand-gold)]/15 border border-[var(--brand-gold-soft)] px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-gold)] animate-pulse" />
                  {row.status}
                </div>
                <p className="text-sm leading-relaxed text-[var(--brand-ink)]">{row.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Service commitment — safety + partnerships. Same voice as the
            "Ben, the Rope, and Coming Home" article. */}
        <section
          data-testid="service-commitment"
          className="max-w-5xl mx-auto px-6 lg:px-10 mt-20"
        >
          <span className="font-eyebrow">Our Best-Service Policy</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--brand-forest)]">
            Come home to tell the tale.
          </h2>
          <p className="mt-4 max-w-3xl text-[var(--brand-muted)] text-lg leading-relaxed">
            The hills and lochs are to be respected. Every order that leaves us
            goes out with a small <em>Coming Home</em> card carrying our number,
            the essentials of the <a
              href="https://www.outdooraccess-scotland.scot/"
              target="_blank"
              rel="noopener noreferrer"
              className="link-expand font-semibold text-[var(--brand-forest)]"
            >Scottish Outdoor Access Code</a>, and a QR to <Link
              to="/guides"
              className="link-expand font-semibold text-[var(--brand-forest)]"
            >our live directory of local partners</Link> &mdash; regional-park
            ranger contacts and mountain-guide partners across Cairngorms,
            Loch Lomond &amp; The Trossachs, and beyond.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              {
                title: 'Tell a loved one first',
                body: 'Route, start time, planned way home, ETA \u2014 written down. If you\u2019re late, someone knows where to send help.',
              },
              {
                title: 'Never a loch alone',
                body: 'Water is the humblest and most patient hazard in Scotland. No solo dips \u2014 nothing about that is worth it.',
              },
              {
                title: 'Weather, twice',
                body: 'Check the night before and again at the door. If the forecast asks you to change plan, change plan. The hill will still be there.',
              },
              {
                title: 'Ranger before you ride',
                body: 'Heading somewhere new? Ring the local park ranger. Heading somewhere serious? Hire a guide. Cheap insurance, best local knowledge.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="border-l-2 border-[var(--brand-gold)] bg-[var(--brand-forest)]/5 px-5 py-4"
              >
                <div className="font-display text-lg text-[var(--brand-forest-deep)]">{item.title}</div>
                <p className="mt-1 text-sm leading-relaxed text-[var(--brand-ink)]">{item.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm italic text-[var(--brand-muted)] max-w-3xl">
            If you&rsquo;re a Scottish mountain guide, ranger service, or regional
            park partner and would like to be on the Coming Home card for your
            patch,{' '}
            <Link
              to="/#contact"
              data-testid="sourcing-guide-partner-cta"
              className="link-expand font-semibold text-[var(--brand-forest)]"
            >
              get in touch with our team
            </Link>
            &nbsp;&mdash; we&rsquo;re building the list.
          </p>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-20">
          <div className="bg-[var(--brand-forest)] text-[var(--brand-parchment)] p-10 relative overflow-hidden">
            <div className="grain absolute inset-0 opacity-30" />
            <div className="relative">
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--brand-gold)]">Have a question about a supplier?</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl">Talk with our team.</h2>
              <p className="mt-4 max-w-xl text-[var(--brand-parchment)]/90 leading-relaxed">
                We&rsquo;re happy to walk you through where a specific product comes from, the
                test reports we hold on it, and what your first-year experience will be.
                No sales script &mdash; just a Gamekeeper&rsquo;s judgement.
              </p>
              <Link
                to="/#contact"
                data-testid="sourcing-contact-cta"
                className="btn-gold mt-6 inline-flex px-6 py-3 text-sm font-semibold tracking-wide items-center gap-2 rounded-none"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
